import React from 'react'
import Helmet from 'react-helmet'

import JsonLD, { JsonLDType, JsonLDBreadcrumbList, JsonLDOrganization } from '@cinch/oxd-components/json-ld/src'

import { Breadcrumb, SEOContainerProps } from './seo.container'

interface SEOProps extends SEOContainerProps {
  siteMetadata: Cinch.SiteMetadata
}

const getBreadcrumbList = (breadcrumbs: Breadcrumb[]): JsonLDBreadcrumbList['schema'] => ({
  itemListElement: breadcrumbs.map((crumb, index) => ({
    type: JsonLDType.ListItem,
    schema: { ...crumb, position: index + 1 },
  })),
})

const SEO: React.FC<SEOProps> = ({ articleSchema, breadcrumbs, desc, noIndex, siteMetadata, title }) => {
  const seo = {
    title: siteMetadata.defaultTitle,
    description: desc || siteMetadata.defaultDescription,
  }

  if (title) {
    seo.title = `${title} - ${siteMetadata.defaultTitle}`
  }

  const org: JsonLDOrganization['schema'] = {
    address: {
      type: JsonLDType.PostalAddress,
      schema: siteMetadata.postalAddress,
    },
    contactPoint: {
      type: JsonLDType.ContactPoint,
      schema: siteMetadata.contactPoint,
    },
    description: siteMetadata.defaultDescription,
    legalName: siteMetadata.legalName,
    logo: {
      type: JsonLDType.ImageObject,
      schema: {
        url: siteMetadata.staticLogo,
      },
    },
    name: siteMetadata.defaultTitle,
    sameAs: siteMetadata.socialLinks.map((link) => link.href),
    url: siteMetadata.siteUrl,
  }

  if (articleSchema && !articleSchema.publisher) {
    articleSchema.publisher = {
      type: JsonLDType.Org,
      schema: org,
    }
  }

  if (articleSchema && !articleSchema.author) {
    articleSchema.author = {
      type: JsonLDType.Org,
      schema: org,
    }
  }

  const homeBreadcrumb = {
    item: siteMetadata.siteUrl,
    name: siteMetadata.defaultTitle,
  }

  const breadcrumbList =
    breadcrumbs && breadcrumbs.length >= 1 ? getBreadcrumbList([homeBreadcrumb, ...breadcrumbs]) : undefined

  return (
    <>
      <Helmet>
        <html lang={siteMetadata.siteLanguage} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {noIndex && <meta name="robots" content="noindex" />}
      </Helmet>
      {/* Helmet doesnt allow custom components, even if they return the correct primitive element i.e. a script tag */}
      <JsonLD schema={org} type={JsonLDType.Org} />
      {articleSchema && <JsonLD schema={articleSchema} type={JsonLDType.Article} />}
      {breadcrumbList && <JsonLD schema={breadcrumbList} type={JsonLDType.BreadcrumbList} />}
    </>
  )
}

export default SEO
